import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { register } from '../../../services/authService';
import { validateEmail, validatePassword, validateRequired } from '../../../utils/validators';
import { ROLES } from '../../../utils/constants';
import { getAllDepartments } from '../../../supabase/database';
import colleges from '../../../data/collegesData';
import './SignupForm.css';

const SignupForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    password: '',
    confirmPassword: '',
    registrationType: 'user', // 'user' or 'department_head'
    departmentId: ''
  });
  const [departments, setDepartments] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        // Fetch actual department data from database to get UUIDs
        const dbDepartments = await getAllDepartments();

        // Create a map of department name to ID
        const deptMap = {};
        dbDepartments.forEach(dept => {
          deptMap[dept.name] = dept.id;
        });

        // Collect all departments from collegesData and match with DB IDs
        const allDepts = [];
        colleges.forEach(college => {
          if (Array.isArray(college.departments)) {
            college.departments.forEach(dept => {
              // Only include if department exists in database
              if (deptMap[dept.name]) {
                allDepts.push({
                  id: deptMap[dept.name], // UUID from database
                  name: dept.name,
                  college: college.name,
                  programs: dept.programs || []
                });
              }
            });
          }
        });
        // Sort alphabetically by department name
        allDepts.sort((a, b) => a.name.localeCompare(b.name));
        setDepartments(allDepts);
      } catch (error) {
        console.error('Error fetching departments:', error);
        setError('Failed to load departments. Please refresh the page.');
      }
    };

    fetchDepartments();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!validateRequired(formData.displayName)) {
      newErrors.displayName = 'Name is required';
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.errors.join('. ');
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (formData.registrationType === 'department_head' && !formData.departmentId) {
      newErrors.departmentId = 'Please select a department';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const role = formData.registrationType === 'department_head'
        ? ROLES.DEPARTMENT_HEAD
        : ROLES.USER;

      const departmentId = formData.registrationType === 'department_head'
        ? formData.departmentId
        : null;

      // Register user - department heads will need admin approval
      await register(
        formData.email,
        formData.password,
        formData.displayName,
        role,
        departmentId,
        formData.registrationType === 'department_head' // pending approval for dept heads
      );

      if (formData.registrationType === 'department_head') {
        setSuccess('Registration successful! Your account is pending admin approval. You will be notified once approved.');
        // Reset form
        setFormData({
          displayName: '',
          email: '',
          password: '',
          confirmPassword: '',
          registrationType: 'user',
          departmentId: ''
        });
        // Redirect after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Regular users can login immediately
        navigate('/login');
      }
    } catch (error) {
      setError(error.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="signup-form-card">
      <Card.Body>
        {error && <Alert variant="danger" className="mb-2 py-1 footer-text">{error}</Alert>}
        {success && <Alert variant="success" className="mb-2 py-1 footer-text">{success}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-2">
            <Form.Label className="fw-bold text-primary small mb-1">Registration Type</Form.Label>
            <div className="d-flex gap-4">
              <Form.Check
                type="radio"
                label="Authenticated User"
                name="registrationType"
                id="registration-user"
                value="user"
                checked={formData.registrationType === 'user'}
                onChange={(e) => setFormData({ ...formData, registrationType: e.target.value, departmentId: '' })}
                className="mb-1 small"
              />
              <Form.Check
                type="radio"
                label="Department Head"
                name="registrationType"
                id="registration-dept-head"
                value="department_head"
                checked={formData.registrationType === 'department_head'}
                onChange={(e) => setFormData({ ...formData, registrationType: e.target.value })}
                className="mb-1 small"
              />
            </div>
          </Form.Group>

          {formData.registrationType === 'department_head' && (
            <Form.Group className="mb-2" controlId="departmentId">
              <Form.Label className="small mb-1">Department</Form.Label>
              <Form.Select
                name="departmentId"
                value={formData.departmentId}
                onChange={handleChange}
                isInvalid={!!errors.departmentId}
                size="sm"
              >
                <option value="">Select Department</option>
                {departments.map((dept, idx) => (
                  <option key={idx} value={dept.id}>{dept.name} ({dept.college})</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.departmentId}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Row>
            <Col md={6}>
              <Form.Group className="mb-2" controlId="displayName">
                <Form.Label className="small mb-1">Full Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your full name"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  isInvalid={!!errors.displayName}
                  size="sm"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.displayName}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2" controlId="email">
                <Form.Label className="small mb-1">Email address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  size="sm"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-2" controlId="password">
                <Form.Label className="small mb-1">Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  isInvalid={!!errors.password}
                  size="sm"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-2" controlId="confirmPassword">
                <Form.Label className="small mb-1">Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  isInvalid={!!errors.confirmPassword}
                  size="sm"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-grid gap-2 mt-3">
            <Button variant="primary" type="submit" disabled={loading} size="sm">
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </div>
        </Form>

        <div className="text-center mt-3">
          <p>
            Already have an account? <Link to="/login">Login</Link>
          </p>
        </div>
      </Card.Body>
    </Card>
  );
};

export default SignupForm;